import type {RunOutcome} from './novaInvocationStore';

// Why this task cannot be actioned, as a property of the task and the person looking at it.
//
// 'no_edit_task' covers both halves of "may not act on this task": the person does not hold the
// edit-tasks permission, or they are a client user restricted from task actions. See
// deriveEligibilityFromTask for why those are one reason rather than two.
export type EligibilityReason = 'task_locked' | 'no_edit_task';

// A disabled control can also be blocked by run capacity rather than the task's own eligibility —
// once three tasks are running at once, every other idle control disables itself until one
// finishes. 'run_cap_reached' widens the disabled reason to cover that alongside eligibility.
//
// 'no_agent_available' is the third such reason: nothing about this task, and nothing about this
// person's permissions — there is simply no agent in the resolved list for a run to use. Its own
// member rather than anything phrased about the person, because no agent carries a role
// restriction: attributing an empty list to who the person is would state a cause that does not
// exist.
// 'out_of_credits' is the account's AI allowance being spent: nothing about this task or person,
// and it lifts by itself when the allowance resets or is topped up.
//
// Every one of these is temporary and clears on its own, which is what earns them a disabled
// control with the reason on it. An EligibilityReason is deliberately NOT one of them: a standing
// permission never resolves from this screen, and a disabled control the person can never enable
// is a defect rather than an explanation. Those produce 'hidden' or 'view_only' instead.
//
// 'last_run_failed' is the modal header's alone: the conversation's newest turn is an error nobody
// has retried or dismissed, so there is nothing to continue from until they do.
export type DisabledReason = 'run_cap_reached' | 'no_agent_available' | 'out_of_credits' | 'last_run_failed';

// The message every surface gives for each reason. Each one names a cause the person can tell apart
// from the others: Nova is busy with three already, there is no agent to run, the allowance is
// spent, the last run needs dealing with. Shared by the control and the in-task card beside it,
// which must never give two different reasons for the same refusal.
export const DISABLED_REASON_MESSAGE_ID: Record<DisabledReason, string> = {
	run_cap_reached: 'nova_invocation.control.reason_run_cap',
	no_agent_available: 'nova_invocation.control.reason_no_agent_available',
	out_of_credits: 'nova_invocation.control.reason_out_of_credits',
	last_run_failed: 'nova_invocation.control.reason_last_run_failed',
};

export type ControlState =
	// Nothing renders at all. Where a standing permission applies and there is no answer to read,
	// the absence of the control is the whole state - no greyed button, no banner, no explanation.
	| {kind: 'hidden'}
	// A single View button: an answer exists and this person may read it, but may not start, steer,
	// stop or end anything. Never the filled Ready treatment and never the unread tint, which exist
	// to pull somebody towards output that is theirs to act on.
	| {kind: 'view_only'}
	| {kind: 'action'}
	| {kind: 'working'}
	| {kind: 'ready_or_continue'; read: boolean}
	| {kind: 'disabled'; reason: DisabledReason};
// Reserved, not rendered in v1 — see Design decisions above:
// | { kind: 'needs_input' } | { kind: 'actioned' } | { kind: 'failed' }

// Whether there is a finished answer on this task to read. A run still going is not one: it has
// produced nothing yet, and a run somebody else started is not this person's to be shown. Nor is a
// run that was stopped - it ended with nothing - unless a conversation from before it survives.
function hasAnswerToRead(input: {
	hasEverRun: boolean;
	isRunning: boolean;
	hasConversation?: boolean;
	outcome?: RunOutcome | null;
}): boolean {
	// An earlier conversation stays readable while a newer run is in flight: the run adds to it, it
	// does not take it away.
	if (input.hasConversation === true) return true;
	if (input.isRunning) return false;
	return input.hasEverRun && input.outcome?.kind !== 'cancelled';
}

export function deriveControlState(input: {
	hasEverRun: boolean;
	isRunning: boolean;
	outputRead: boolean;
	eligibility: EligibilityReason | null;
	atRunCap?: boolean;
	// Which terminal outcome a finished run reached. A failure is not a row state of its own — what
	// went wrong is legible where the conversation renders, not summarised on the task row — but it
	// is still a result the person has not seen, so the row offers it as Ready rather than hiding it.
	// A stop is the exception, and the only outcome read here: the person ended that run themselves,
	// so there is nothing to announce - the control offers the conversation if there is one, Action if
	// not.
	outcome?: RunOutcome | null;
	// Whether the resolved agent list has anything in it (novaInvocationStore.hasAgentToRun). A bare
	// Action click runs the first agent in that list, so with an empty one there is nothing to run
	// and the click does nothing at all — no state change, no notification. That is worse than a
	// stated disabled reason in every case, and in two of them it is not even momentary: an account
	// with the flag on and no granted agents, and a run-service blip that degrades the list to `[]`,
	// both leave a live-looking control permanently dead.
	//
	// Optional, defaulting to "there is one": absent information must not disable a control, and a
	// caller that does not model the agent list at all should get the behaviour it had before.
	hasAgent?: boolean;
	// Whether the task already has a conversation on the server, from Task.hasNovaConversation.
	// hasEverRun above is session state: it is true only for a run this browser tab started and is
	// gone on the next page load, so without this a task whose conversation is sitting right there
	// offers to start a fresh one every time it is reloaded. A discarded conversation is not one -
	// the field is false for a task whose newest thread was discarded, so discarding returns the
	// control to Action, which is what discarding is for.
	//
	// Optional, defaulting to "not known": a caller that does not select the field keeps exactly the
	// behaviour it had before rather than being told there is no conversation.
	hasConversation?: boolean;
	// Whether the account's AI allowance is spent (useNovaCreditsBlock). Below every reason that is
	// about the task or person, above the run cap: a run that cannot be paid for is the truer reason
	// than one that is merely waiting for a slot. Optional, defaulting to "not blocked".
	creditsBlocked?: boolean;
	// Whether the server says a run is working on this task right now (Task.novaRunningRunId) -
	// started in another tab, before a reload, or by a teammate. This tab's own run is `isRunning`.
	// Optional, defaulting to "not known", which keeps the behaviour a caller had before.
	serverRunning?: boolean;
}): ControlState {
	// A standing permission is not advertised. Acting on Nova is gated on being able to edit the
	// task; reading it never is - so what a blocked person is left with is the one thing they can
	// do, and nothing else is drawn to tell them about the things they cannot. Which of the two
	// eligibility reasons applies makes no difference here: neither is resolvable from this screen,
	// so neither earns a reason on the page.
	if (input.eligibility) return hasAnswerToRead(input) ? {kind: 'view_only'} : {kind: 'hidden'};
	// Working wherever the run was started. Offering Action mid-run let a second, paid run replace
	// the conversation the first was about to answer.
	if (input.isRunning || input.serverRunning) return {kind: 'working'};
	// A finished run this tab started stays readable whatever happens afterwards - its answer exists.
	// A failed run is readable in the same way and reads as Ready until opened: the thread holds what
	// was asked and what Nova said went wrong. A run the person stopped is not here: they ended it
	// themselves, so there is nothing new to announce.
	if (input.hasEverRun && input.outcome?.kind !== 'cancelled') {
		return {kind: 'ready_or_continue', read: input.outputRead};
	}
	// A task with a conversation always offers it. Continue only opens the conversation, so nothing
	// about starting a run - the allowance, the run limit, the agent list - may take it away, and a
	// stopped run never turns it back into Action, which would start a new conversation and hide
	// this one. Read as already seen: a conversation loaded from the server is one the person has
	// already met, so continuing it is the offer, not an unread badge.
	if (input.hasConversation) return {kind: 'ready_or_continue', read: true};
	// Everything below is about starting a run, which is all Action does.
	//
	// The agent check is ahead of the run limit because the limit's own copy tells the person to wait
	// for a run to finish - true only if a run would then be possible, which with no agent it would
	// not be.
	if (input.hasAgent === false) return {kind: 'disabled', reason: 'no_agent_available'};
	if (input.creditsBlocked) return {kind: 'disabled', reason: 'out_of_credits'};
	if (input.atRunCap) return {kind: 'disabled', reason: 'run_cap_reached'};
	return {kind: 'action'};
}

// The modal header's control starts a run from Continue as well as from Action, so a spent
// allowance disables both there. Rows and cards keep Continue, which only opens the task.
export function blockHeaderForCredits(state: ControlState, creditsBlocked: boolean): ControlState {
	if (!creditsBlocked) return state;
	if (state.kind === 'action' || state.kind === 'ready_or_continue') return {kind: 'disabled', reason: 'out_of_credits'};
	return state;
}

// The modal header's Continue, while the conversation's newest turn is an error still to be retried
// or dismissed. A conversation cannot go on from a run that never finished, so Continue waits until
// the error is dealt with - the same rule the composer applies to Reply to Nova. Rows and cards keep
// Continue: it only opens the task, which is how the person reaches the error in the first place.
export function blockHeaderForFailedTurn(state: ControlState, newestTurnFailed: boolean): ControlState {
	if (!newestTurnFailed || state.kind !== 'ready_or_continue') return state;
	return {kind: 'disabled', reason: 'last_run_failed'};
}
